import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Link as MuiLink,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ReauthDialog from "./ReauthDialog";
import MfaEnrollSteps from "./MfaEnrollSteps";
import { getAuth, multiFactor } from "firebase/auth";

const STEP_MANAGE = "manage";
const STEP_ENROLLING = "enrolling";

/**
 * MfaEnrollDialog — MFA enrollment and management dialog.
 * When already enrolled: shows factors and "Switch to a Different Method" (atomic replace).
 * No option to disable MFA entirely; users must add a new method first, then the old one is removed.
 */
const MfaEnrollDialog = ({
  open,
  onClose,
  onEnrolled,
  enrolledFactors: propEnrolledFactors = [],
  source = "default",
}) => {
  const { t } = useTranslation("account");
  const [step, setStep] = useState(STEP_MANAGE);
  const [enrolledFactors, setEnrolledFactors] = useState(propEnrolledFactors);
  const [reauthOpen, setReauthOpen] = useState(false);
  const pendingActionRef = useRef(null);
  const factorsToUnenrollRef = useRef([]);

  const auth = getAuth();

  useEffect(() => {
    if (!open) return;
    setEnrolledFactors(propEnrolledFactors);
    setStep(propEnrolledFactors.length > 0 ? STEP_MANAGE : STEP_ENROLLING);
    factorsToUnenrollRef.current = [];
  }, [open, propEnrolledFactors]);

  const requireReauth = useCallback((action) => {
    pendingActionRef.current = action;
    setReauthOpen(true);
  }, []);

  const handleReauthSuccess = useCallback(async () => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    if (action) await action();
  }, []);

  const handleSwitchMethod = () => {
    factorsToUnenrollRef.current = [...enrolledFactors];
    setStep(STEP_ENROLLING);
  };

  const getFactorSummary = (factor) => {
    if (factor.factorId === "totp") {
      return t("mfaEnroll.usingTotp");
    }

    const phoneText = factor.phoneNumber || t("mfaEnroll.enrolledPhone");
    return t("mfaEnroll.usingSms", { phone: phoneText });
  };

  const handleEnrollDone = useCallback(async () => {
    const toUnenroll = factorsToUnenrollRef.current;
    if (toUnenroll.length > 0) {
      try {
        const mf = multiFactor(auth.currentUser);
        for (const factor of toUnenroll) {
          await mf.unenroll(factor);
        }
      } catch (err) {
        if (err.code === "auth/requires-recent-login") {
          requireReauth(handleEnrollDone);
          return;
        }
      }
      factorsToUnenrollRef.current = [];
    }
    onEnrolled?.();
    onClose();
  }, [auth.currentUser, onEnrolled, onClose, requireReauth]);

  const resetAndClose = () => {
    setStep(propEnrolledFactors.length > 0 ? STEP_MANAGE : STEP_ENROLLING);
    factorsToUnenrollRef.current = [];
    onClose();
  };

  const isSwitching = factorsToUnenrollRef.current.length > 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={resetAndClose}
        maxWidth={step === STEP_ENROLLING ? "sm" : "xs"}
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {step === STEP_MANAGE
            ? t("mfaEnroll.manageTitle")
            : isSwitching
              ? t("mfaEnroll.switchTitle")
              : t("mfaEnroll.setupTitle")}
        </DialogTitle>

        <DialogContent>
          {step === STEP_MANAGE && (
            <Box>
              {enrolledFactors.map((factor) => (
                <DialogContentText key={factor.uid} sx={{ mb: 2 }}>
                  {getFactorSummary(factor)}
                </DialogContentText>
              ))}

              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {t("mfaEnroll.cannotDisable")}
              </Typography>

              <Button
                variant="outlined"
                fullWidth
                onClick={handleSwitchMethod}
                sx={{ mb: 2 }}
              >
                {t("mfaEnroll.switchButton")}
              </Button>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center" }}
              >
                {t("mfaEnroll.lostDevice")}{" "}
                <MuiLink href="mailto:support@md3c.com" underline="hover">
                  {t("common.contactSupport")}
                </MuiLink>
              </Typography>
            </Box>
          )}

          {step === STEP_ENROLLING && (
            <MfaEnrollSteps
              title={
                isSwitching
                  ? t("mfaEnroll.switchStepTitle")
                  : t("mfaEnroll.setupStepTitle")
              }
              subtitle={
                isSwitching
                  ? t("mfaEnroll.switchSubtitle")
                  : t("mfaEnroll.requiredSubtitle")
              }
              showMethodSelectHeader={!(source === "settings" && isSwitching)}
              showBackOnMethodSelect={
                enrolledFactors.length > 0 &&
                !(source === "settings" && isSwitching)
              }
              onCancel={
                enrolledFactors.length > 0
                  ? () => setStep(STEP_MANAGE)
                  : undefined
              }
              onDone={handleEnrollDone}
              onRequireReauth={requireReauth}
            />
          )}
        </DialogContent>
      </Dialog>

      <ReauthDialog
        open={reauthOpen}
        onClose={() => setReauthOpen(false)}
        onSuccess={handleReauthSuccess}
      />
    </>
  );
};

export default MfaEnrollDialog;
